import { Button, Card, CardContent, Input, Label } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { KeyRound, LogIn, LogOut } from "lucide-react";
import { useState, type FormEvent } from "react";

import { ApiError, displayError } from "../api/errors.js";
import { signOut } from "../api/auth.js";
import { setActiveOrganisationHeader } from "../api/client.js";
import { Brand } from "../components/brand.js";
import { ErrorState, LoadingState } from "../components/states.js";
import {
  getSharedDeviceRoster,
  unbindSharedDevice,
  unlockSharedDevice,
} from "./quick-access-api.js";

export function LockScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const roster = useQuery({
    queryFn: getSharedDeviceRoster,
    queryKey: ["shared-device-roster"],
    retry: false,
  });
  const unlock = useMutation({
    mutationFn: () => unlockSharedDevice(selectedId!, pin),
    onSuccess: async () => {
      setActiveOrganisationHeader(roster.data!.org_id);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await navigate({ to: "/" });
    },
  });
  const remove = useMutation({
    mutationFn: unbindSharedDevice,
    onSuccess: async () => {
      setActiveOrganisationHeader(null);
      queryClient.clear();
      await navigate({ to: "/sign-in" });
    },
  });
  const addAccount = useMutation({
    mutationFn: async () => {
      try {
        await signOut();
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 401)) throw error;
      }
    },
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/sign-in" });
    },
  });

  if (roster.isPending) {
    return <main className="grid min-h-dvh place-items-center bg-canvas p-5"><LoadingState label="Opening quick access" /></main>;
  }
  if (roster.error) {
    if (roster.error instanceof ApiError && roster.error.status === 401) {
      return <Navigate replace to="/sign-in" />;
    }
    return <main className="grid min-h-dvh place-items-center bg-canvas p-5"><ErrorState message={displayError(roster.error)} onAction={() => void roster.refetch()} title="Quick access is unavailable" /></main>;
  }

  const selected = roster.data.profiles.find(
    (profile) => profile.staff_profile_id === selectedId,
  );
  return (
    <main className="min-h-dvh bg-canvas px-5 py-8 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between gap-4">
          <Brand />
          <Button disabled={remove.isPending} onClick={() => remove.mutate()} size="sm" variant="ghost">
            <LogOut className="size-4" aria-hidden="true" /> Use another workplace
          </Button>
        </div>
        <header className="mt-12 text-center">
          <p className="text-xs font-bold tracking-widest text-emerald-800 uppercase">{roster.data.org_name}</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight">Who’s using SafePlate?</h1>
          <p className="mt-2 text-sm text-stone-600">Choose your name, then enter your six-digit PIN.</p>
        </header>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {roster.data.profiles.map((profile) => (
            <button
              aria-pressed={selectedId === profile.staff_profile_id}
              className={`min-h-24 rounded-xl border bg-surface p-4 text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-800 ${selectedId === profile.staff_profile_id ? "border-emerald-800 ring-2 ring-emerald-800/15" : "border-stone-200 hover:border-stone-300"}`}
              key={profile.staff_profile_id}
              onClick={() => { setSelectedId(profile.staff_profile_id); setPin(""); unlock.reset(); }}
              type="button"
            >
              <span className="grid size-10 place-items-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-900">
                {profile.display_name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}
              </span>
              <span className="mt-3 block font-bold">{profile.display_name}</span>
              <span className="mt-1 block text-xs capitalize text-stone-500">{profile.roles.join(" · ")}</span>
            </button>
          ))}
        </div>
        <div className="mt-6 flex justify-center">
          <Button
            disabled={addAccount.isPending}
            onClick={() => addAccount.mutate()}
            variant="outline"
          >
            <LogIn className="size-4" aria-hidden="true" /> Log in another user
          </Button>
        </div>
        {addAccount.error ? <p className="mt-3 text-center text-sm text-red-800">{displayError(addAccount.error)}</p> : null}
        {roster.data.profiles.length === 0 ? (
          <Card className="mt-8"><CardContent className="p-7 text-center"><h2 className="font-bold">No users on this browser yet</h2><p className="mt-2 text-sm text-stone-600">Log in with email and password to add a person.</p></CardContent></Card>
        ) : null}
        {selected ? (
          <Card className="mx-auto mt-6 max-w-md">
            <CardContent className="p-6">
              <form onSubmit={(event: FormEvent) => { event.preventDefault(); unlock.mutate(); }}>
                <Label htmlFor="quick-pin">PIN for {selected.display_name}</Label>
                <Input
                  autoComplete="off"
                  autoFocus
                  className="mt-2 text-center text-2xl tracking-[0.35em]"
                  id="quick-pin"
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  pattern="[0-9]{6}"
                  required
                  type="password"
                  value={pin}
                />
                {unlock.error ? <p className="mt-3 text-sm font-semibold text-red-800" role="alert">{displayError(unlock.error)}</p> : null}
                <Button className="mt-5 w-full" disabled={pin.length !== 6 || unlock.isPending} type="submit">
                  <KeyRound className="size-4" aria-hidden="true" /> {unlock.isPending ? "Checking…" : "Unlock"}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </main>
  );
}
